<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import { Modal } from '@Shelly/shelly-design-system/index';
	import VideoPlayer from '../../CameraPlayer/VideoPlayer.svelte';
	import HLSPlayer from '../../CameraPlayer/HLSPlayer.svelte';
	import {
		RecordingMediaType,
		type CloudRecording,
		type CloudRecordingVideo,
	} from '../../types/cloud-recordings';
	import CameraThumbnail from '../../CameraThumbnail.svelte';
	import ImageStateContainer from './ImageStateContainer.svelte';
	import { dateFormat } from 'src/helpers/date_helper';
	import { createResignGuard, getSignedMediaUrl } from 'src/services/cam-cloud/media-auth';
	import { isHlsRecording } from '../../helpers/camera-recordings';
	import { resolveMasterKey } from '../../helpers/encryption/master-key-management';
	import { camera_storage_passphrases } from '../../stores/camera_storage_passphrases';
	import ZoomableContainer from '../../CameraPlayer/ZoomableContainer.svelte';

	export let recording: CloudRecording;
	/** The camera this preview belongs to; only its owner can unlock its recordings. */
	export let device: Device = null;

	/** Asks an ancestor to show the shared password prompt, as `HLSPlayer` does. */
	const dispatch = createEventDispatcher<{
		requirekey: { deviceId: string; wrongKey: boolean; date: string };
	}>();

	let showFullscreenModal = false;
	let fullscreenMedia: CloudRecording = null;

	let videoUrl = '';
	let imageLocked = false;

	$: isImage = recording?.type === RecordingMediaType.IMAGE;

	// `$camera_storage_passphrases` is read only to re-run this once an era key is
	// preserved (unlock/provision), which is what swaps the image in.
	$: void gateEncryptedImage(recording, !!device?.shared, $camera_storage_passphrases);

	/**
	 * An image has no player to gate on the decryption key, so the preview resolves
	 * it here: without the recording's era key it offers Unlock instead of showing
	 * a bare lock over the placeholder. A guest of a shared camera owns no master
	 * key, so it keeps the plain lock and no key backup is fetched for it.
	 */
	const gateEncryptedImage = async (
		rec: CloudRecording,
		shared: boolean,
		_preservedKeys?: unknown,
	) => {
		if (shared || rec?.type !== RecordingMediaType.IMAGE || !rec.encrypted || !rec.mac) {
			imageLocked = false;
			return;
		}
		const masterKey = await resolveMasterKey(rec.mac, rec.date);
		// A recording selected while resolving has a run of its own.
		if (rec.id !== recording?.id) return;
		imageLocked = !masterKey;
	};

	const handleUnlock = () => {
		dispatch('requirekey', {
			deviceId: recording?.mac ?? '',
			wrongKey: false,
			date: recording?.date ?? '',
		});
	};

	/**
	 * HLSPlayer's loader signs (and decrypts) every playlist request itself, so an
	 * HLS source must be handed over unsigned.
	 */
	$: isHls = isHlsRecording(recording);
	$: sourceVideoUrl = (recording as CloudRecordingVideo)?.videoUrl ?? '';

	$: {
		if (sourceVideoUrl && !isHls) {
			getSignedMediaUrl(sourceVideoUrl).then((url) => (videoUrl = url));
		} else {
			videoUrl = '';
		}
	}

	const resignMedia = createResignGuard();

	/** A rejected signed query surfaces as a plain media error; re-mint and re-sign once. */
	const handleVideoError = async () => {
		const rejectedUrl = videoUrl;
		if (!(await resignMedia(sourceVideoUrl, rejectedUrl))) return;
		// A source change during the re-mint has already replaced the src.
		if (videoUrl !== rejectedUrl) return;
		videoUrl = await getSignedMediaUrl(sourceVideoUrl);
	};

	const handleImageClick = () => {
		fullscreenMedia = recording;
		showFullscreenModal = true;
	};

	const handleCloseModal = () => {
		showFullscreenModal = false;
		fullscreenMedia = null;
	};

	$: formattedDate = fullscreenMedia ? dateFormat(fullscreenMedia.date, 'd M H:i:s') : '';
</script>

<div class="placeholder-container" class:image={isImage && !imageLocked} class:video={!isImage}>
	{#if recording?.type === RecordingMediaType.VIDEO && isHls}
		<HLSPlayer
			borderRadius="big"
			videoSrc={sourceVideoUrl}
			encrypted={!!recording.encrypted}
			keyUri={recording.keyUri ?? ''}
			deviceId={recording.mac}
			recordingId={recording.id}
			recordingDate={recording.date ?? ''}
			on:requirekey
		/>
	{:else if recording?.type === RecordingMediaType.VIDEO && videoUrl}
		<VideoPlayer borderRadius="big" videoSrc={videoUrl} on:error={handleVideoError} />
	{:else if isImage && imageLocked}
		<ImageStateContainer
			icon="fal fa-lock"
			heading={$format('camera_recording_encrypted', { default: 'Encrypted recording' })}
			ctaTitle={$format('camera_recording_key_submit', { default: 'Unlock' })}
			on:cta={handleUnlock}
		/>
	{:else if isImage}
		<CameraThumbnail
			size="full"
			aspectRatio="16-9"
			deviceId={recording.mac}
			recId={recording.id}
			recDate={recording.date}
			src={recording.imageUrls?.large}
			imageSize="large"
			encrypted={!!recording.encrypted}
			on:click={handleImageClick}
		/>
	{:else}
		<CameraThumbnail size="full" aspectRatio="16-9" />
	{/if}
</div>

<Modal
	on:close={handleCloseModal}
	showModal={showFullscreenModal}
	title={formattedDate}
	backdropClose={true}
>
	<svelte:fragment slot="content">
		{#if fullscreenMedia}
			<ZoomableContainer>
				<CameraThumbnail
					size="full"
					aspectRatio="16-9"
					deviceId={fullscreenMedia.mac}
					recId={fullscreenMedia.id}
					recDate={fullscreenMedia.date}
					src={fullscreenMedia.imageUrls?.large}
					imageSize="large"
					encrypted={!!fullscreenMedia.encrypted}
				/>
			</ZoomableContainer>
		{/if}
	</svelte:fragment>
</Modal>

<style>
	.placeholder-container {
		aspect-ratio: 16/9;
		width: 100%;
		border-radius: var(--radius-big);
		overflow: hidden;
	}

	.placeholder-container.image {
		cursor: pointer;
	}
</style>
